import { useState } from 'react';
import ReactDOM from 'react-dom';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { Chart, LineElement, PointElement, LineController, CategoryScale, LinearScale, Title, Tooltip, Legend, TimeScale } from 'chart.js';

import GraphDetail from './GraphDetail';

// Register Chart.js components
Chart.register(LineElement, PointElement, LineController, CategoryScale, LinearScale, Title, Tooltip, Legend, TimeScale);

interface Props {
  title: string;
  data: any;
  isLoading: boolean;
  isError: boolean;
}

function LineGraph({ title, data, isLoading, isError }: Props) {
  const [selectedGraph, setSelectedGraph] = useState<any>(null);

  // Placeholder data for loading or error state
  const placeholderData = {
    labels: [],
    datasets: [
      {
        label: 'Loading...',
        data: [],
        borderColor: 'rgba(0, 0, 0, 0.1)',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        fill: false,
      },
    ],
  };

  const options = {
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'day' as 'day',
          displayFormats: {
            day: 'MMM d',
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          minRotation: 0,
          maxRotation: 0,
        },
      },
      y: {
        beginAtZero: false,
        stepSize: 10,
        ticks: {
          precision: 0, // Ensures that tick values are displayed as integers
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as 'top' | 'left' | 'right' | 'center' | 'chartArea',
        align: 'start' as "center" | "start" | "end" | undefined,
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          pointStyle: 'rectRounded',
        },
      },
    },
  };

  // Check if data is empty
  const isDataEmpty = !isLoading && !isError && (!data || data.datasets.every((dataset: any) => !dataset.data || dataset.data.length === 0));

  return (
    <>
      <div onClick={() => setSelectedGraph({ title, data, options })} style={styles.container}>
        <h3 style={styles.graphTitle}>{title}</h3>
        <div style={styles.yLabel}>Count</div>
        <div style={styles.graphContainer}>
          <Line data={isLoading || isError || isDataEmpty ? placeholderData : data} options={options} />
          {isLoading && <div style={styles.loadingOverlay}>Loading...</div>}
          {isError && <div style={styles.errorOverlay}>Failed to load data. Please try again later.</div>}
          {isDataEmpty && <div style={styles.emptyDataOverlay}>No data available.<br />Try adjusting the dashboard time range.</div>}
        </div>
      </div>

      {selectedGraph && (
        ReactDOM.createPortal(
          <>
            <div style={styles.backdrop} onClick={() => setSelectedGraph(null)}>
              <div onClick={(e) => e.stopPropagation()}>
                <GraphDetail graph={selectedGraph} onClose={() => setSelectedGraph(null)} />
              </div>
            </div>
          </>
        , document.body)
      )}
    </>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'relative',
  },
  graphTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 10,
  },
  yLabel: {
    margin: 0,
    fontSize: 12,
  },
  loadingOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 16,
    color: 'gray',
    zIndex: 10,
    pointerEvents: 'none',
  },
  errorOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 16,
    color: 'red',
    zIndex: 10,
    textAlign: 'center',
    pointerEvents: 'none',
  },
  emptyDataOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 16,
    color: 'gray',
    zIndex: 10,
    textAlign: 'center',
    pointerEvents: 'none',
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 1)",
    zIndex: 100,
  },
  error: {
    textAlign: 'center',
    fontSize: 16,
    padding: '20px',
    color: 'red',
  },
};

export default LineGraph;
